import { useEffect, useRef } from 'react';
import { cameFromBack } from '../utils/backNav';

const SCROLL_KEY = 'pr.scroll.v1';

// In-memory mirror: the truth mid-session (survives SPA nav) and a parse cache.
let mem;

function readAll() {
  if (mem !== undefined) return mem;
  try { mem = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}') || {}; } catch { mem = {}; }
  return mem;
}

function writeAll(next) {
  mem = next;
  try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify(next)); } catch { /* quota / disabled */ }
}

/** The offset saved for `key`, or 0. Exported for tests and for pages that
 *  need to know whether a restore is pending before they fetch. */
export function getSavedScroll(key) {
  const v = readAll()[key];
  return typeof v === 'number' && v > 0 ? v : 0;
}

export function saveScroll(key, y) {
  writeAll({ ...readAll(), [key]: Math.round(y) });
}

/**
 * Keep a list's scroll position across "tap a row → back".
 *
 * Extracted from the featured wall, which worked this out the hard way, and the
 * two things it learned are the whole reason this is a hook rather than four
 * lines in each page:
 *
 *  1. Restoring has to POLL, not fire once. The content arrives asynchronously,
 *     so at mount the document is one viewport tall and scrollTo(0, 4000) does
 *     nothing at all.
 *  2. The poll has to be setTimeout, NOT requestAnimationFrame. rAF callbacks
 *     are starved for a while right after a history navigation commits, which
 *     is precisely when this runs — on rAF the restore simply never fires.
 *
 * `key` scopes the offset, so a page with tabs keeps one position per tab
 * instead of dropping you at People's offset inside Events. Passing a null key
 * disables the hook (for a page that isn't ready to decide its key yet).
 */
export default function useScrollRestore(key, { enabled = true } = {}) {
  // Captured once, at first render, while the popstate signal is still fresh —
  // the list may not mount for seconds, by which point the signal has expired.
  const fromBack = useRef(cameFromBack());
  const keyRef = useRef(key);
  keyRef.current = key;

  // Save continuously while the list is on screen. rAF is correct HERE (we're
  // not mid-navigation) and keeps a scroll event from writing on every frame.
  useEffect(() => {
    if (!enabled || !key) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; saveScroll(keyRef.current, window.scrollY); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, key]);

  // Restore, once, if this mount is the result of going back.
  useEffect(() => {
    if (!enabled || !key || !fromBack.current) return undefined;
    const target = getSavedScroll(key);
    if (target <= 0) return undefined;

    let done = false;
    const timers = [];
    const startedAt = Date.now();
    let lastMax = -1;
    let stableSince = startedAt;

    const attempt = () => {
      if (done) return;
      const now = Date.now();
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      // Tall enough to hold the exact position → land on it.
      if (maxScroll >= target - 2) { window.scrollTo(0, target); done = true; return; }

      // Track when the page stops growing, i.e. content finished arriving.
      if (maxScroll !== lastMax) { lastMax = maxScroll; stableSince = now; }

      // Loaded, but it settled SHORTER than where we were — fewer results came
      // back this time, say. Go as far as the page now allows rather than
      // refusing to move at all.
      if (maxScroll > window.innerHeight && now - stableSince > 500) {
        window.scrollTo(0, Math.min(target, maxScroll));
        done = true;
        return;
      }

      if (now - startedAt < 9000) timers.push(setTimeout(attempt, 100));
    };

    attempt();
    return () => { done = true; timers.forEach(clearTimeout); };
    // Deliberately keyed on the scope only: this must run once per list, and
    // re-running it mid-scroll would yank the user back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  return { cameFromBack: fromBack.current };
}
